import { supabase } from '@/integrations/supabase/client';
import { stripe } from './stripe';

// Remove the direct client creation and use the centralized client
// const supabase = createClient(
//   process.env.VITE_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL || process.env.SUPABASE_URL!,
//   process.env.VITE_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY!
// );

export interface PayoutSchedule {
  id: string;
  sellerId: string;
  schedule: 'daily' | 'weekly' | 'monthly' | 'manual';
  nextPayoutDate: Date;
  minimumAmount: number; // in cents
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface PayoutSummary {
  sellerId: string;
  pendingAmount: number;
  lastPayoutDate?: Date;
  nextPayoutDate?: Date;
  totalPayouts: number;
  successfulPayouts: number;
  failedPayouts: number;
  currency: string;
}

export interface TransactionReport {
  id: string;
  buyerId: string;
  sellerId: string;
  sequenceId: string;
  amount: number;
  platformFee: number;
  sellerAmount: number;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  paymentIntentId: string;
  currency: string;
  createdAt: Date;
  processedAt?: Date;
}

export interface PayoutReport {
  id: string;
  sellerId: string;
  stripePayoutId: string;
  amount: number;
  status: 'pending' | 'paid' | 'failed' | 'canceled';
  currency: string;
  createdAt: Date;
  arrivalDate?: Date;
  failureReason?: string;
  transactionIds: string[];
}

/**
 * Calculate pending payout amount for a seller
 */
export async function calculatePendingPayout(sellerId: string): Promise<number> {
  try {
    // Get all successful transactions that haven't been paid out yet
    const { data: transactions, error } = await supabase
      .from('stripe_transactions')
      .select('amount, platform_fee, id')
      .eq('seller_id', sellerId)
      .eq('status', 'succeeded')
      .is('payout_id', null); // Transactions not yet included in a payout

    if (error) throw error;

    const pendingAmount = transactions?.reduce((sum, tx) => {
      return sum + (tx.amount - tx.platform_fee);
    }, 0) || 0;

    return pendingAmount;
  } catch (error) {
    console.error('Error calculating pending payout:', error);
    throw error;
  }
}

/**
 * Create an instant payout for a seller
 */
export async function createInstantPayout(
  sellerId: string,
  amount?: number
): Promise<{ payoutId: string; expectedArrival: Date }> {
  try {
    // Get seller's Stripe account
    const { data: accountData, error: accountError } = await supabase
      .from('stripe_accounts')
      .select('stripe_account_id')
      .eq('user_id', sellerId)
      .single();

    if (accountError || !accountData) {
      throw new Error('Seller Stripe account not found');
    }

    // Calculate amount if not provided
    let payoutAmount = amount;
    if (!payoutAmount) {
      payoutAmount = await calculatePendingPayout(sellerId);
    }

    if (payoutAmount <= 0) {
      throw new Error('No funds available for payout');
    }

    // Create the payout in Stripe
    const payout = await stripe.payouts.create({
      amount: payoutAmount,
      currency: 'usd',
      method: 'instant', // For instant payouts (additional fees apply)
    }, {
      stripeAccount: accountData.stripe_account_id,
    });

    // Record the payout in our database
    const { data: payoutRecord, error: payoutError } = await supabase
      .from('stripe_payouts')
      .insert({
        stripe_payout_id: payout.id,
        user_id: sellerId,
        amount: payoutAmount,
        currency: 'usd',
        status: payout.status,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (payoutError) throw payoutError;

    // Update transactions to link them to this payout
    const { data: pendingTransactions } = await supabase
      .from('stripe_transactions')
      .select('id')
      .eq('seller_id', sellerId)
      .eq('status', 'succeeded')
      .is('payout_id', null)
      .limit(100); // Reasonable limit

    if (pendingTransactions?.length) {
      await supabase
        .from('stripe_transactions')
        .update({ payout_id: payoutRecord.id })
        .in('id', pendingTransactions.map(tx => tx.id));
    }

    // Calculate expected arrival (instant payouts arrive within 30 minutes)
    const expectedArrival = new Date();
    expectedArrival.setMinutes(expectedArrival.getMinutes() + 30);

    return {
      payoutId: payout.id,
      expectedArrival,
    };
  } catch (error) {
    console.error('Error creating instant payout:', error);
    throw error;
  }
}

/**
 * Create a standard payout for a seller
 */
export async function createStandardPayout(
  sellerId: string,
  amount?: number
): Promise<{ payoutId: string; expectedArrival: Date }> {
  try {
    // Get seller's Stripe account
    const { data: accountData, error: accountError } = await supabase
      .from('stripe_accounts')
      .select('stripe_account_id')
      .eq('user_id', sellerId)
      .single();

    if (accountError || !accountData) {
      throw new Error('Seller Stripe account not found');
    }

    // Calculate amount if not provided
    let payoutAmount = amount;
    if (!payoutAmount) {
      payoutAmount = await calculatePendingPayout(sellerId);
    }

    if (payoutAmount <= 0) {
      throw new Error('No funds available for payout');
    }

    // Create the payout in Stripe
    const payout = await stripe.payouts.create({
      amount: payoutAmount,
      currency: 'usd',
    }, {
      stripeAccount: accountData.stripe_account_id,
    });

    // Record the payout in our database
    const { data: payoutRecord, error: payoutError } = await supabase
      .from('stripe_payouts')
      .insert({
        stripe_payout_id: payout.id,
        user_id: sellerId,
        amount: payoutAmount,
        currency: 'usd',
        status: payout.status,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (payoutError) throw payoutError;

    // Update transactions to link them to this payout
    const { data: pendingTransactions } = await supabase
      .from('stripe_transactions')
      .select('id')
      .eq('seller_id', sellerId)
      .eq('status', 'succeeded')
      .is('payout_id', null)
      .limit(100);

    if (pendingTransactions?.length) {
      await supabase
        .from('stripe_transactions')
        .update({ payout_id: payoutRecord.id })
        .in('id', pendingTransactions.map(tx => tx.id));
    }

    // Calculate expected arrival (standard payouts arrive in 2 business days)
    const expectedArrival = new Date();
    expectedArrival.setDate(expectedArrival.getDate() + 2);

    return {
      payoutId: payout.id,
      expectedArrival,
    };
  } catch (error) {
    console.error('Error creating standard payout:', error);
    throw error;
  }
}

/**
 * Get payout summary for a seller
 */
export async function getPayoutSummary(sellerId: string): Promise<PayoutSummary> {
  try {
    // Get pending amount
    const pendingAmount = await calculatePendingPayout(sellerId);

    // Get payout statistics
    const { data: payouts, error: payoutError } = await supabase
      .from('stripe_payouts')
      .select('status, created_at, amount')
      .eq('user_id', sellerId)
      .order('created_at', { ascending: false });

    if (payoutError) throw payoutError;

    const totalPayouts = payouts?.length || 0;
    const successfulPayouts = payouts?.filter(p => p.status === 'paid').length || 0;
    const failedPayouts = payouts?.filter(p => p.status === 'failed').length || 0;
    const lastPayoutDate = payouts?.[0]?.created_at ? new Date(payouts[0].created_at) : undefined;

    // Calculate next payout date based on schedule (simplified logic)
    let nextPayoutDate: Date | undefined;
    if (pendingAmount > 0) {
      nextPayoutDate = new Date();
      nextPayoutDate.setDate(nextPayoutDate.getDate() + 7); // Weekly default
    }

    return {
      sellerId,
      pendingAmount,
      lastPayoutDate,
      nextPayoutDate,
      totalPayouts,
      successfulPayouts,
      failedPayouts,
      currency: 'usd',
    };
  } catch (error) {
    console.error('Error getting payout summary:', error);
    throw error;
  }
}

/**
 * Get transaction report for a seller
 */
export async function getTransactionReport(
  sellerId: string,
  startDate: Date,
  endDate: Date,
  limit: number = 50
): Promise<TransactionReport[]> {
  try {
    const { data: transactions, error } = await supabase
      .from('stripe_transactions')
      .select(`
        id,
        buyer_id,
        seller_id,
        sequence_id,
        amount,
        platform_fee,
        status,
        stripe_payment_intent_id,
        currency,
        created_at,
        updated_at
      `)
      .eq('seller_id', sellerId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return transactions?.map(tx => ({
      id: tx.id,
      buyerId: tx.buyer_id,
      sellerId: tx.seller_id,
      sequenceId: tx.sequence_id,
      amount: tx.amount,
      platformFee: tx.platform_fee,
      sellerAmount: tx.amount - tx.platform_fee,
      status: tx.status,
      paymentIntentId: tx.stripe_payment_intent_id,
      currency: tx.currency,
      createdAt: new Date(tx.created_at),
      processedAt: tx.updated_at ? new Date(tx.updated_at) : undefined,
    })) || [];
  } catch (error) {
    console.error('Error getting transaction report:', error);
    throw error;
  }
}

/**
 * Get payout report for a seller
 */
export async function getPayoutReport(
  sellerId: string,
  startDate: Date,
  endDate: Date,
  limit: number = 50
): Promise<PayoutReport[]> {
  try {
    const { data: payouts, error } = await supabase
      .from('stripe_payouts')
      .select(`
        id,
        user_id,
        stripe_payout_id,
        amount,
        status,
        currency,
        created_at,
        updated_at
      `)
      .eq('user_id', sellerId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // For each payout, get the associated transaction IDs
    const payoutReports: PayoutReport[] = [];
    
    for (const payout of payouts || []) {
      const { data: transactions } = await supabase
        .from('stripe_transactions')
        .select('id')
        .eq('payout_id', payout.id);

      payoutReports.push({
        id: payout.id,
        sellerId: payout.user_id,
        stripePayoutId: payout.stripe_payout_id,
        amount: payout.amount,
        status: payout.status,
        currency: payout.currency,
        createdAt: new Date(payout.created_at),
        arrivalDate: payout.status === 'paid' && payout.updated_at ? new Date(payout.updated_at) : undefined,
        transactionIds: transactions?.map(tx => tx.id) || [],
      });
    }

    return payoutReports;
  } catch (error) {
    console.error('Error getting payout report:', error);
    throw error;
  }
}

/**
 * Check if automatic payouts should be processed
 */
export async function processAutomaticPayouts(): Promise<{ processed: number; errors: string[] }> {
  try {
    let processed = 0;
    const errors: string[] = [];

    // Get all sellers with automatic payout enabled and pending amounts
    const { data: sellers, error } = await supabase
      .from('stripe_accounts')
      .select('user_id')
      .eq('payouts_enabled', true);

    if (error) throw error;

    for (const seller of sellers || []) {
      try {
        const pendingAmount = await calculatePendingPayout(seller.user_id);
        
        // Only process if minimum threshold is met (e.g., $10)
        if (pendingAmount >= 1000) { // $10 in cents
          await createStandardPayout(seller.user_id, pendingAmount);
          processed++;
        }
      } catch (error) {
        errors.push(`Failed to process payout for seller ${seller.user_id}: ${error}`);
      }
    }

    return { processed, errors };
  } catch (error) {
    console.error('Error processing automatic payouts:', error);
    throw error;
  }
}

/**
 * Retry a failed payout
 */
export async function retryFailedPayout(payoutId: string): Promise<{ success: boolean; newPayoutId?: string }> {
  try {
    // Get the failed payout details
    const { data: payout, error } = await supabase
      .from('stripe_payouts')
      .select('user_id, amount, currency')
      .eq('id', payoutId)
      .eq('status', 'failed')
      .single();

    if (error || !payout) {
      throw new Error('Failed payout not found');
    }

    // Create a new payout with the same amount
    const result = await createStandardPayout(payout.user_id, payout.amount);
    
    return {
      success: true,
      newPayoutId: result.payoutId,
    };
  } catch (error) {
    console.error('Error retrying failed payout:', error);
    return { success: false };
  }
} 